import './equipmentPopup.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Icon from '../../01_atoms/icons/Icon';

const hasHtml = (value = '') => /<\/?[a-z][\s\S]*>/i.test(value);

const renderUspContent = (uspList) => {
  if (!uspList || !String(uspList).trim()) return null;

  if (hasHtml(uspList)) {
    return <div className="equipment-popup__usp-html" dangerouslySetInnerHTML={{ __html: uspList }} />;
  }

  const items = String(uspList)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!items.length) return null;

  return (
    <ul className="equipment-popup__usp-list">
      {items.map((item, index) => (
        <li key={`usp-${index}`}>{item}</li>
      ))}
    </ul>
  );
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

export default function EquipmentPopup({
  open,
  onClose,
  items = [],
  initialItemId,
  models = [],
  modelTitle = '',
}) {
  const [activeItemId, setActiveItemId] = useState(initialItemId);
  const bodyRef = useRef(null);
  const accordionRefs = useRef({});
  const lastOpenedItemIdRef = useRef(initialItemId);
  const intl = useIntl();

  const displayItems = useMemo(
    () => (Array.isArray(items) ? items.filter((item) => item && item.id != null) : []),
    [items],
  );

  const uspTitleById = useMemo(() => {
    const map = new Map();
    displayItems.forEach((item) => {
      map.set(String(item.id), String(item.title || '').trim());
    });
    return map;
  }, [displayItems]);

  const getHierarchyLabel = (item) => {
    const parentIds = Array.isArray(item?.parent_usp_id) ? item.parent_usp_id : [];
    const parentTitles = parentIds
      .map((id) => uspTitleById.get(String(id)))
      .filter(Boolean);

    if (!parentTitles.length) return '';

    const prefix = intl.formatMessage({ id: 'powertrain.equipment_hierarchy_prefix' });
    return `${prefix} ${parentTitles.join(' + ')}`;
  };

  useEffect(() => {
    if (open) {
      setActiveItemId(initialItemId);
      lastOpenedItemIdRef.current = initialItemId;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open, initialItemId]);

  useEffect(() => {
    if (activeItemId != null && String(activeItemId).trim() !== '') {
      lastOpenedItemIdRef.current = activeItemId;
    }
  }, [activeItemId]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !activeItemId) return undefined;

    const scrollToAccordionTitle = () => {
      const body = bodyRef.current;
      const accordion = accordionRefs.current[String(activeItemId)];
      if (!body || !accordion) return;

      const trigger = accordion.querySelector('.equipment-popup__accordion-trigger');
      const target = trigger || accordion;
      const bodyTop = body.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const nextTop = body.scrollTop + (targetTop - bodyTop);

      body.scrollTo({
        top: Math.max(0, nextTop),
        behavior: 'smooth',
      });
    };

    const timer = window.setTimeout(scrollToAccordionTitle, 320);
    return () => window.clearTimeout(timer);
  }, [open, activeItemId]);

  const headerTitle = useMemo(() => {
    const uspId = activeItemId != null && String(activeItemId).trim() !== ''
      ? activeItemId
      : lastOpenedItemIdRef.current;

    const matchedModel = (Array.isArray(models) ? models : []).find(
      (model) => sameId(model?.car_usp_id, uspId),
    );

    return matchedModel?.title || modelTitle || '';
  }, [activeItemId, models, modelTitle]);

  if (!open || !displayItems.length) return null;

  const onAccordionClick = (itemId) => {
    setActiveItemId((current) => (sameId(current, itemId) ? null : itemId));
  };

  return (
    <div className="equipment-popup" role="dialog" aria-modal="true">
      <button type="button" className="equipment-popup__backdrop" aria-label="Close" onClick={onClose} />

      <div className="equipment-popup__dialog">
        <button type="button" className="equipment-popup__close equipment-popup__close--desktop" onClick={onClose} aria-label="Close">
          <Icon icon="close-2026" className="equipment-popup__close-icon-2026" aria-hidden="true" focusable="false" />
        </button>

        <div className="equipment-popup__header">
          <div className="equipment-popup__header-main">
            <h2 className="equipment-popup__title">{headerTitle}</h2>
          </div>
        </div>

        <div className="equipment-popup__body" ref={bodyRef}>
          {displayItems.map((item) => {
            const isOpen = sameId(item.id, activeItemId);
            const hierarchy = getHierarchyLabel(item);

            return (
              <div
                key={item.id}
                ref={(node) => {
                  const key = String(item.id);
                  if (node) accordionRefs.current[key] = node;
                  else delete accordionRefs.current[key];
                }}
                className={`equipment-popup__accordion${isOpen ? ' equipment-popup__accordion--open' : ''}`}
              >
                <button
                  type="button"
                  className="equipment-popup__accordion-trigger"
                  onClick={() => onAccordionClick(item.id)}
                  aria-expanded={isOpen}
                >
                  <div className="equipment-popup__accordion-heading">
                    <span className="equipment-popup__accordion-title">{item.title}</span>
                    {hierarchy ? (
                      <span className="equipment-popup__accordion-hierarchy">{hierarchy}</span>
                    ) : null}
                  </div>
                  <span className="equipment-popup__accordion-icon" aria-hidden="true" />
                </button>

                <div
                  className={`equipment-popup__accordion-collapse${isOpen ? ' equipment-popup__accordion-collapse--open' : ''}`}
                >
                  <div className="equipment-popup__accordion-collapse-inner">
                    <div className="equipment-popup__accordion-content">
                      {renderUspContent(item.usp_list)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="equipment-popup__footer">
          <div className="equipment-popup__footer-info">
            <p className="equipment-popup__footer-label">
              <FormattedMessage id="powertrain.full_equipment.footer" />
            </p>
            <p className="equipment-popup__footer-model">{headerTitle}</p>
          </div>

          <button type="button" className="equipment-popup__close equipment-popup__close--mobile" onClick={onClose} aria-label="Close">
            {/* <Close className="close-2026" icon="close-2026" aria-hidden="true" focusable="false" /> */}
            <Icon icon="close-2026" className="equipment-popup__close-icon-2026" aria-hidden="true" focusable="false" />
          </button>
        </div>
      </div>
    </div>
  );
}
